import { useState, useCallback } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import { Stack, TextField, InputAdornment, IconButton, Grid, FormLabel, FormControl, Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { changePassword } from '../../services/user.service';
import { ChangePasswordSchema } from '../../validations/ChangePasswordSchema';

export default function ChangePassword() {
    const [showPassword, setShowPassword] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);

    const formik = useFormik({
        initialValues: {
            oldPassword: '',
            password: '',
            confirmNewPassword: ''
        },
        validationSchema: ChangePasswordSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            setSubmitting(true)
            await changePassword(values.id, values).then(() => {
                setSubmitting(false);
                resetForm();
            }).catch((err) => {
                setSubmitting(false)
            })
        }
    });

    const handleShowOldPassword = useCallback(() => {
        setShowOldPassword((show) => !show);
    }, []);

    const handleShowPassword = useCallback(() => {
        setShowPassword((show) => !show);
    }, []);

    const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

    return (
        <Box>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={8}>
                            <Stack spacing={3}>
                                <FormControl fullWidth>
                                    <FormLabel sx={{ mb: 1, fontWeight: 600, fontSize: '14px', color: '#374151' }}>Old Password</FormLabel>
                                    <TextField
                                        {...getFieldProps('oldPassword')}
                                        fullWidth
                                        autoComplete="off"
                                        type={showOldPassword ? 'text' : 'password'}
                                        placeholder="Enter current password"
                                        InputProps={{
                                            sx: { borderRadius: '10px' },
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleShowOldPassword} edge="end" size='small'>
                                                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={Boolean(touched.oldPassword && errors.oldPassword)}
                                        helperText={(touched.oldPassword && errors.oldPassword)}
                                    />
                                </FormControl>
                                
                                <FormControl fullWidth>
                                    <FormLabel sx={{ mb: 1, fontWeight: 600, fontSize: '14px', color: '#374151' }}>New Password</FormLabel>
                                    <TextField
                                        {...getFieldProps('password')}
                                        fullWidth
                                        autoComplete="off"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Enter new password"
                                        InputProps={{
                                            sx: { borderRadius: '10px' },
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleShowPassword} edge="end" size='small'>
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={Boolean(touched.password && errors.password)}
                                        helperText={(touched.password && errors.password)}
                                    />
                                </FormControl>

                                <FormControl fullWidth>
                                    <FormLabel sx={{ mb: 1, fontWeight: 600, fontSize: '14px', color: '#374151' }}>Confirm New Password</FormLabel>
                                    <TextField
                                        {...getFieldProps('confirmNewPassword')}
                                        fullWidth
                                        autoComplete="off"
                                        type="password"
                                        placeholder="Confirm new password"
                                        InputProps={{ sx: { borderRadius: '10px' } }}
                                        error={Boolean(touched.confirmNewPassword && errors.confirmNewPassword)}
                                        helperText={touched.confirmNewPassword && errors.confirmNewPassword}
                                    />
                                </FormControl>

                                <Box sx={{ pt: 2 }}>
                                    <LoadingButton
                                        type="submit"
                                        variant="contained"
                                        loading={isSubmitting}
                                        sx={{
                                            py: 1.5,
                                            px: 4,
                                            borderRadius: '10px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            backgroundColor: '#2259c9',
                                            '&:hover': {
                                                backgroundColor: '#1a44a0',
                                            }
                                        }}
                                    >
                                        Update Password
                                    </LoadingButton>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ p: 3, bgcolor: '#F9FAFB', borderRadius: '12px', border: '1px solid #F3F4F6' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#111827' }}>
                                    Password Requirements
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ color: '#6B7280', mb: 0.5 }}>
                                    • Minimum 8 characters
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ color: '#6B7280', mb: 0.5 }}>
                                    • At least one uppercase letter
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ color: '#6B7280', mb: 0.5 }}>
                                    • At least one number or special character
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Form>
            </FormikProvider>
        </Box>
    );
}
